import { useSelector } from 'react-redux'

import { UserPermissionsResponseDto } from '../../../api/kmsapiclient'
import * as permissionsApi from '../../../api/permissionsApi'
import {
  ADD_KEY_MAPPINGS_PERMISSION,
  ADD_USAGE_PERMISSION,
  DELETE_KEY_MAPPINGS_PERMISSION,
  DELETE_KEYS_PERMISSION,
  DELETE_USAGE_PERMISSION,
  GET_KEY_MAPPINGS_PERMISSION,
  GET_KEYS_PERMISSION,
  GET_USAGE_PERMISSION,
  UPDATE_KEY_MAPPINGS_PERMISSION,
  UPDATE_KEYS_PERMISSION,
  UPDATE_USAGE_PERMISSION,
  UPLOAD_KEYS_PERMISSION,
} from '../../../redux/actions/actionTypes'
import {
  apiCallError,
  beginApiCall,
} from '../../../redux/actions/apiStatusActions'
import { getUserPermissionsSuccess } from '../../../redux/actions/permissionActions'
import { useAppDispatch } from '../../../redux/configureStore'
import { IRootState } from '../../../redux/reducers'
import {
  IdentityState,
  UserPermissionsState,
} from '../../../redux/reducers/initialState'

export const usePermissions = () => {
  const dispatch = useAppDispatch()
  const identity: IdentityState | null = useSelector(
    (state: IRootState) => state.identity,
  )
  const userPermissionsState: UserPermissionsState | null = useSelector(
    (state: IRootState) => state.userPermissions,
  )

  const isLoginCompleted = identity !== null && userPermissionsState !== null

  /**
   * List of permissions required for accessing the Keys page.
   * Each permission corresponds to a key in the UserPermissionsState interface.
   */
  const permissionsForKeysPages: (keyof UserPermissionsState)[] = [
    'hasGetKeysAccess',
    'hasUpdateKeysAccess',
    'hasDeleteKeysAccess',
    'hasUploadKeysAccess',
  ]

  /**
   * List of permissions required for accessing the Permissions page.
   * Each permission corresponds to a key in the UserPermissionsState interface.
   */
  const permissionsForPermissionsPage: (keyof UserPermissionsState)[] = [
    'hasGetPermissionsAccess',
    'hasAddPermissionsAccess',
    'hasDeletePermissionsAccess',
    'hasUpdatePermissionsAccess',
  ]

  /**
   * List of permissions required for accessing the Entra ID Roles page.
   * Each permission corresponds to a key in the UserPermissionsState interface.
   */
  const permissionsForEntraIdRolesPage: (keyof UserPermissionsState)[] = [
    'hasEntraIDRolesAccess',
  ]

  function checkPermissions(state: IRootState, permissionType: string) {
    if (!state.identity || !state.userPermissions) {
      throw new Error('Please Sign In')
    }

    switch (permissionType) {
      case GET_KEYS_PERMISSION:
        if (!state.userPermissions.hasGetKeysAccess)
          throw new Error('Insufficient Permission to Get Keys')
        break
      case DELETE_KEYS_PERMISSION:
        if (!state.userPermissions.hasDeleteKeysAccess)
          throw new Error('Insufficient Permission to Delete Keys')
        break
      case UPDATE_KEYS_PERMISSION:
        if (!state.userPermissions.hasUpdateKeysAccess)
          throw new Error('Insufficient Permission to Update Keys')
        break
      case UPLOAD_KEYS_PERMISSION:
        if (!state.userPermissions.hasUploadKeysAccess)
          throw new Error('Insufficient Permission to Upload Keys')
        break
      case GET_USAGE_PERMISSION:
        if (!state.userPermissions.hasGetPermissionsAccess)
          throw new Error('Insufficient Permission to Get Usage Permissions')
        break
      case GET_KEY_MAPPINGS_PERMISSION:
        if (!state.userPermissions.hasGetPermissionsAccess)
          throw new Error('Insufficient Permission to Get Key Mappings')
        break
      case ADD_KEY_MAPPINGS_PERMISSION:
        if (!state.userPermissions.hasAddPermissionsAccess)
          throw new Error('Insufficient Permission to Add Key Mappings')
        break
      case ADD_USAGE_PERMISSION:
        if (!state.userPermissions.hasAddPermissionsAccess)
          throw new Error('Insufficient Permission to Add Usage Permissions')
        break
      case DELETE_KEY_MAPPINGS_PERMISSION:
        if (!state.userPermissions.hasDeletePermissionsAccess)
          throw new Error('Insufficient Permission to Delete Key Mappings')
        break
      case DELETE_USAGE_PERMISSION:
        if (!state.userPermissions.hasDeletePermissionsAccess)
          throw new Error('Insufficient Permission to Delete Usage Permissions')
        break
      case UPDATE_KEY_MAPPINGS_PERMISSION:
        if (!state.userPermissions.hasUpdatePermissionsAccess)
          throw new Error('Insufficient Permission to Update Key Mappings')
        break
      case UPDATE_USAGE_PERMISSION:
        if (!state.userPermissions.hasUpdatePermissionsAccess)
          throw new Error('Insufficient Permission to Update Usage Permissions')
        break
      default:
        throw new Error('Missing Permission to Perform Action')
    }
  }

  const getUserPermissions = async () => {
    dispatch(beginApiCall())
    try {
      const userPermissions: UserPermissionsResponseDto =
        await permissionsApi.getUserPermissionsAsync()
      if (userPermissions) {
        dispatch(getUserPermissionsSuccess(userPermissions))
      }
    } catch (error) {
      dispatch(apiCallError())
      throw error
    }
  }

  return {
    permissionsForKeysPages,
    permissionsForPermissionsPage,
    permissionsForEntraIdRolesPage,
    checkPermissions,
    getUserPermissions,
    isLoginCompleted,
    userPermissionsState,
  }
}
